import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Filter, X, Calendar } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const pad = (v) => String(v).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function todayISO() { return daysAgoISO(0); }

const PRESETS = [
  { key: "today",    label: "Today",     from: () => todayISO(),      to: () => todayISO() },
  { key: "7d",       label: "Last 7d",   from: () => daysAgoISO(6),   to: () => todayISO() },
  { key: "30d",      label: "Last 30d",  from: () => daysAgoISO(29),  to: () => todayISO() },
  { key: "90d",      label: "Last 90d",  from: () => daysAgoISO(89),  to: () => todayISO() },
];

const MACRO_FILTERS = [
  { key: "min_calories",  label: "Cal ≥",   unit: "",   color: "text-emerald-400" },
  { key: "min_protein_g", label: "Prot ≥",  unit: "g",  color: "text-rose-400" },
  { key: "min_carbs_g",   label: "Carb ≥",  unit: "g",  color: "text-blue-400" },
  { key: "min_fat_g",     label: "Fat ≥",   unit: "g",  color: "text-orange-400" },
  { key: "min_sugar_g",   label: "Sug ≥",   unit: "g",  color: "text-pink-400" },
  { key: "min_fiber_g",   label: "Fib ≥",   unit: "g",  color: "text-violet-400" },
  { key: "min_sodium_mg", label: "Na ≥",    unit: "mg", color: "text-slate-400" },
];

/**
 * Filterable nutrition history — date range presets + custom range + per-macro minimums.
 * Shows a per-day rollup on top and expandable entries underneath.
 */
export default function NutritionHistoryPanel() {
  const [dateFrom, setDateFrom] = useState(daysAgoISO(6));
  const [dateTo, setDateTo] = useState(todayISO());
  const [preset, setPreset] = useState("7d");
  const [macroMins, setMacroMins] = useState({});
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ entries: [], days: [], totals: null });
  const [expandedDay, setExpandedDay] = useState(null);

  const applyPreset = (p) => {
    setPreset(p.key);
    setDateFrom(p.from());
    setDateTo(p.to());
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, limit: 300 };
      for (const [k, v] of Object.entries(macroMins)) {
        if (v && Number(v) > 0) params[k] = Number(v);
      }
      const { data } = await api.get("/nutrition/history", { params });
      setData(data || { entries: [], days: [], totals: null });
    } catch (err) {
      toast.error(formatApiError(err));
      setData({ entries: [], days: [], totals: null });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, macroMins]);

  useEffect(() => { load(); }, [load]);

  const activeFilterCount = useMemo(
    () => Object.values(macroMins).filter((v) => Number(v) > 0).length,
    [macroMins]
  );

  const clearMacroFilters = () => setMacroMins({});

  const entriesByDay = useMemo(() => {
    const map = new Map();
    for (const e of data.entries) {
      if (!map.has(e.day)) map.set(e.day, []);
      map.get(e.day).push(e);
    }
    return map;
  }, [data.entries]);

  return (
    <div
      className="bg-white border border-slate-200 p-5"
      data-testid="nutrition-history-panel"
    >
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[var(--amino-teal)]">// History</div>
          <div className="font-display font-black uppercase tracking-tight text-2xl leading-none mt-1 flex items-center gap-2">
            <Filter className="w-5 h-5 text-[#0D9488]" /> Filtered Log
          </div>
        </div>
        <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 text-right">
          {loading ? "Loading…" : `${data.totals?.count ?? 0} entries · ${data.days.length} days`}
        </div>
      </div>

      {/* Date preset row */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {PRESETS.map((p) => {
          const active = preset === p.key;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              className={[
                "border px-3 py-1.5 font-mono-num text-[10px] uppercase tracking-widest transition-colors",
                active
                  ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                  : "border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:hover:text-white",
              ].join(" ")}
              data-testid={`history-preset-${p.key}`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Custom date range */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div>
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num flex items-center gap-1">
            <Calendar className="w-2.5 h-2.5" /> From
          </Label>
          <Input
            type="date"
            value={dateFrom}
            max={dateTo}
            onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
            className="bg-slate-50 border-slate-200 rounded-none h-9 font-mono-num text-xs"
            data-testid="history-date-from"
          />
        </div>
        <div>
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num flex items-center gap-1">
            <Calendar className="w-2.5 h-2.5" /> To
          </Label>
          <Input
            type="date"
            value={dateTo}
            min={dateFrom}
            max={todayISO()}
            onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
            className="bg-slate-50 border-slate-200 rounded-none h-9 font-mono-num text-xs"
            data-testid="history-date-to"
          />
        </div>
      </div>

      {/* Macro-min filters */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <Label className="text-[9px] uppercase tracking-widest text-slate-500 font-mono-num">Macro filters (min)</Label>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearMacroFilters}
              className="flex items-center gap-1 font-mono-num text-[9px] uppercase tracking-widest text-slate-500 hover:text-slate-900 dark:hover:text-white"
              data-testid="history-clear-filters"
            >
              <X className="w-2.5 h-2.5" /> Clear {activeFilterCount}
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-1.5">
          {MACRO_FILTERS.map((f) => (
            <div key={f.key}>
              <div className={`font-mono-num text-[9px] uppercase tracking-widest ${f.color} mb-0.5`}>{f.label} {f.unit}</div>
              <Input
                type="number" step="0.1" min="0" inputMode="decimal"
                value={macroMins[f.key] ?? ""}
                onChange={(e) => setMacroMins((m) => ({ ...m, [f.key]: e.target.value }))}
                placeholder="0"
                className="bg-slate-50 border-slate-200 rounded-none h-8 font-mono-num text-xs px-2"
                data-testid={`history-filter-${f.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Range totals */}
      {data.totals && data.totals.count > 0 && (
        <div className="mb-4 border border-slate-200 bg-slate-50 p-3" data-testid="history-range-totals">
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-2">// Range totals</div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-mono-num text-xs">
            <span className="text-emerald-400 tabular-nums">{Math.round(data.totals.calories).toLocaleString()}<span className="text-slate-500 ml-0.5">kcal</span></span>
            <span className="text-rose-400 tabular-nums">{Math.round(data.totals.protein_g)}<span className="text-slate-500 ml-0.5">g P</span></span>
            <span className="text-blue-400 tabular-nums">{Math.round(data.totals.carbs_g)}<span className="text-slate-500 ml-0.5">g C</span></span>
            <span className="text-orange-400 tabular-nums">{Math.round(data.totals.fat_g)}<span className="text-slate-500 ml-0.5">g F</span></span>
            <span className="text-pink-400 tabular-nums">{Math.round(data.totals.sugar_g)}<span className="text-slate-500 ml-0.5">g Sug</span></span>
            <span className="text-violet-400 tabular-nums">{Math.round(data.totals.fiber_g)}<span className="text-slate-500 ml-0.5">g Fib</span></span>
            <span className="text-slate-400 tabular-nums">{Math.round(data.totals.sodium_mg)}<span className="text-slate-500 ml-0.5">mg Na</span></span>
          </div>
        </div>
      )}

      {/* Per-day results */}
      {loading ? (
        <div className="text-center py-8 text-slate-500 text-sm">Loading…</div>
      ) : data.days.length === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm border border-dashed border-slate-200" data-testid="history-empty">
          No entries match these filters.
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="history-days-list">
          {data.days.map((day) => {
            const rows = entriesByDay.get(day.day) || [];
            const expanded = expandedDay === day.day;
            return (
              <div key={day.day} className="border border-slate-200 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setExpandedDay(expanded ? null : day.day)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50/50 transition-colors text-left"
                  data-testid={`history-day-${day.day}`}
                >
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-display font-bold text-slate-900 dark:text-white text-sm">
                      {new Date(day.day + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                    </span>
                    <span className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500">
                      · {day.count} item{day.count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-x-2 font-mono-num text-[11px] shrink-0">
                    <span className="text-emerald-400 tabular-nums">{Math.round(day.calories)}<span className="text-slate-400 ml-0.5">kcal</span></span>
                    <span className="text-rose-400 tabular-nums">{Math.round(day.protein_g)}<span className="text-slate-400 ml-0.5">gP</span></span>
                    <span className="text-slate-400 tabular-nums">{Math.round(day.sodium_mg)}<span className="text-slate-400 ml-0.5">Na</span></span>
                  </div>
                </button>
                {expanded && (
                  <div className="divide-y divide-zinc-900 border-t border-slate-200" data-testid={`history-day-entries-${day.day}`}>
                    {rows.map((e) => (
                      <div key={e.id} className="px-3 py-2 flex items-baseline gap-3">
                        <div className="font-mono-num text-[11px] text-slate-500 w-14 shrink-0">
                          {new Date(e.logged_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        <div className="flex-1 min-w-0">
                          {e.note && <div className="text-sm text-slate-900 dark:text-white truncate">{e.note}</div>}
                          <div className="flex flex-wrap items-baseline gap-x-2 font-mono-num text-[10px]">
                            <span className="text-emerald-400 tabular-nums">{Math.round(e.calories)}<span className="text-slate-400 ml-0.5">kcal</span></span>
                            {e.protein_g > 0 && <span className="text-rose-400 tabular-nums">{Math.round(e.protein_g)}<span className="text-slate-400 ml-0.5">gP</span></span>}
                            {e.carbs_g > 0 && <span className="text-blue-400 tabular-nums">{Math.round(e.carbs_g)}<span className="text-slate-400 ml-0.5">gC</span></span>}
                            {e.fat_g > 0 && <span className="text-orange-400 tabular-nums">{Math.round(e.fat_g)}<span className="text-slate-400 ml-0.5">gF</span></span>}
                            {e.sodium_mg > 0 && <span className="text-slate-400 tabular-nums">{Math.round(e.sodium_mg)}<span className="text-slate-400 ml-0.5">mgNa</span></span>}
                          </div>
                        </div>
                        {e.meal_group && (
                          <span className="font-mono-num text-[9px] uppercase tracking-widest text-slate-500 shrink-0">
                            {e.meal_group}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          onClick={load}
          disabled={loading}
          className="h-8 bg-slate-50 hover:bg-slate-200 text-slate-800 rounded-none border border-slate-200 uppercase tracking-widest text-[10px] font-mono-num"
          data-testid="history-refresh"
        >
          Refresh
        </Button>
      </div>
    </div>
  );
}
